// Diagnostic report contract (ato-pwa viewer side).
//
// Scope: failure reports for a *capsule run on a runner* — NOT ato-pwa/ato-web's
// own JS exceptions. A report classifies whether the failure is the capsule's
// (toml/repo), Ato's (runtime), the provider's, or the user's environment.
//
// The PWA is a pure read/viewer surface. It consumes SEALED reports only and is
// fed already-redacted summaries + log chunks — raw plaintext logs and crash
// dumps never reach the client (the type surface deliberately has no field for
// them). The canonical producers (runner / ato-api) own redaction, fingerprint,
// D1 index, R2 blobs and the chunk content addressing.
//
// Read endpoints (see ato-api#38):
//   GET /v1/diagnostics/reports?run_id=…           → DiagnosticReportSummary[]
//   GET /v1/diagnostics/reports/:id                → DiagnosticSummary
//   GET /v1/diagnostics/reports/:id/manifest       → DiagnosticManifest (ETag)
//   GET /v1/diagnostics/reports/:id/chunks/:chunkId → jsonl text (content-addressed)

/** Fixed ownership/where-it-broke vocabulary. Open type so a newer server code
 *  is still rendered honestly rather than dropped. */
export type AtoErrorClass =
  | "capsule_manifest_error"
  | "recipe_resolution_error"
  | "artifact_materialization_error"
  | "runtime_install_error"
  | "provider_capacity_error"
  | "provider_policy_error"
  | "network_bind_error"
  | "permission_denied"
  | "process_crash"
  | "healthcheck_failed"
  | "qa_environment_error"
  | "ato_internal_error"
  | (string & {});

export type DiagnosticSeverity = "info" | "warning" | "error" | "fatal" | (string & {});

/** A report is only viewable once sealed (`ready`). `open`/`sealing` are still
 *  uploading; `failed`/`expired` have no readable body. */
export type DiagnosticStatus = "open" | "sealing" | "ready" | "failed" | "expired" | (string & {});

/** Who the failure most likely belongs to (shown in the summary). */
export type LikelyOwner = "toml" | "repo" | "ato" | "provider" | "user_env" | (string & {});

export type DiagnosticStream = "stdout" | "stderr" | "lifecycle" | "provider";

export const DIAGNOSTIC_STREAMS: readonly DiagnosticStream[] = [
  "stdout",
  "stderr",
  "lifecycle",
  "provider",
];

/** One row of GET /v1/diagnostics/reports — the list, served from the D1 index
 *  only (no body is fetched to build it). */
export interface DiagnosticReportSummary {
  report_id: string;
  /** The run this failure belongs to — the canonical join key. */
  run_id: string;
  /** The true execution-receipt id (blake3), when the run emitted one. */
  execution_id: string | null;
  status: DiagnosticStatus;
  severity: DiagnosticSeverity;
  error_class: AtoErrorClass | null;
  capsule_ref: string | null;
  created_at: string | null;
  sealed_at: string | null;
}

/** GET /v1/diagnostics/reports/:id — the redacted, summary-first document shown
 *  before any log chunk is loaded. */
export interface DiagnosticSummary {
  report_id: string;
  run_id: string;
  execution_id: string | null;
  status: DiagnosticStatus;
  severity: DiagnosticSeverity;
  error_class: AtoErrorClass | null;
  /** Which layer most likely caused the failure. */
  likely_owner: LikelyOwner | null;
  /** First observed failure line/title (already redacted). */
  first_failure: string | null;
  /** Actionable next step suggested by the classifier. */
  suggested_next_action: string | null;
  /** Short human summary (redacted). */
  message: string | null;
}

/** Chunk metadata from the manifest. The body lives at the content-addressed R2
 *  key; `chunk_id` === sha256(ciphertext) and is the cache key. */
export interface DiagnosticChunkMeta {
  chunk_id: string;
  stream: DiagnosticStream;
  seq: number;
  sha256: string;
  compressed_bytes: number | null;
  first_timestamp: string | null;
  last_timestamp: string | null;
}

/** GET /v1/diagnostics/reports/:id/manifest — the chunk index + receipt ref. */
export interface DiagnosticManifest {
  report_id: string;
  status: DiagnosticStatus;
  chunks: DiagnosticChunkMeta[];
  /** Whether a (redacted) receipt is available for download. */
  has_receipt: boolean;
}

/** A decoded jsonl log line. The viewer renders `text`; `ts`/`level` are
 *  optional metadata when the producer emitted structured lines. */
export interface DiagnosticLogLine {
  ts: string | null;
  level: string | null;
  text: string;
}

// ── Low-level coercion helpers (mirrors api/runs.ts) ──────────────────────────

function asRecord(v: unknown): Record<string, unknown> | null {
  return v && typeof v === "object" && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : null;
}

function stringOr(v: unknown): string | undefined {
  return typeof v === "string" && v.length > 0 ? v : undefined;
}

function numberOr(v: unknown): number | undefined {
  return typeof v === "number" && Number.isFinite(v) ? v : undefined;
}

function normalizeStream(v: unknown): DiagnosticStream | null {
  return v === "stdout" || v === "stderr" || v === "lifecycle" || v === "provider" ? v : null;
}

// ── Public normalizers ────────────────────────────────────────────────────────

function normalizeReportSummary(raw: unknown): DiagnosticReportSummary | null {
  const r = asRecord(raw);
  const report_id = stringOr(r?.report_id ?? r?.id);
  if (!report_id) return null;
  return {
    report_id,
    run_id: stringOr(r?.run_id) ?? "",
    execution_id: stringOr(r?.execution_id) ?? null,
    status: (stringOr(r?.status) ?? "ready") as DiagnosticStatus,
    severity: (stringOr(r?.severity) ?? "error") as DiagnosticSeverity,
    error_class: (stringOr(r?.error_class) ?? null) as AtoErrorClass | null,
    capsule_ref: stringOr(r?.capsule_ref) ?? null,
    created_at: stringOr(r?.created_at) ?? null,
    sealed_at: stringOr(r?.sealed_at) ?? null,
  };
}

/** Normalize the `{ reports: [...] }` envelope from GET /v1/diagnostics/reports. */
export function normalizeReportList(raw: unknown): DiagnosticReportSummary[] {
  const r = asRecord(raw);
  const list = Array.isArray(r?.reports) ? r.reports : Array.isArray(raw) ? raw : [];
  return list
    .map(normalizeReportSummary)
    .filter((x): x is DiagnosticReportSummary => x !== null);
}

export function normalizeSummary(raw: unknown): DiagnosticSummary {
  const r = asRecord(raw) ?? {};
  return {
    report_id: stringOr(r.report_id ?? r.id) ?? "",
    run_id: stringOr(r.run_id) ?? "",
    execution_id: stringOr(r.execution_id) ?? null,
    status: (stringOr(r.status) ?? "ready") as DiagnosticStatus,
    severity: (stringOr(r.severity) ?? "error") as DiagnosticSeverity,
    error_class: (stringOr(r.error_class) ?? null) as AtoErrorClass | null,
    likely_owner: (stringOr(r.likely_owner) ?? null) as LikelyOwner | null,
    first_failure: stringOr(r.first_failure) ?? null,
    suggested_next_action: stringOr(r.suggested_next_action) ?? null,
    message: stringOr(r.message) ?? null,
  };
}

function normalizeChunkMeta(raw: unknown): DiagnosticChunkMeta | null {
  const r = asRecord(raw);
  const chunk_id = stringOr(r?.chunk_id ?? r?.id);
  const stream = normalizeStream(r?.stream);
  if (!chunk_id || !stream) return null;
  return {
    chunk_id,
    stream,
    seq: numberOr(r?.seq) ?? 0,
    sha256: stringOr(r?.sha256) ?? chunk_id,
    compressed_bytes: numberOr(r?.compressed_bytes) ?? null,
    first_timestamp: stringOr(r?.first_timestamp) ?? null,
    last_timestamp: stringOr(r?.last_timestamp) ?? null,
  };
}

export function normalizeManifest(raw: unknown): DiagnosticManifest {
  const r = asRecord(raw) ?? {};
  const chunksRaw = Array.isArray(r.chunks) ? r.chunks : [];
  const chunks = chunksRaw
    .map(normalizeChunkMeta)
    .filter((c): c is DiagnosticChunkMeta => c !== null)
    // Stable order: by stream, then sequence — the viewer renders in this order.
    .sort((a, b) => (a.stream === b.stream ? a.seq - b.seq : a.stream.localeCompare(b.stream)));
  return {
    report_id: stringOr(r.report_id ?? r.id) ?? "",
    status: (stringOr(r.status) ?? "ready") as DiagnosticStatus,
    chunks,
    has_receipt: r.has_receipt === true,
  };
}

/** Decode a jsonl chunk body into log lines. Each non-empty line is either a
 *  JSON object ({ ts?, level?, msg|text|message }) or raw text; malformed lines
 *  are surfaced verbatim rather than dropped (honest over lossy). */
export function decodeChunkLines(body: string): DiagnosticLogLine[] {
  const out: DiagnosticLogLine[] = [];
  for (const line of body.split("\n")) {
    if (line.length === 0) continue;
    let parsed: Record<string, unknown> | null = null;
    if (line.startsWith("{")) {
      try {
        parsed = asRecord(JSON.parse(line));
      } catch {
        parsed = null;
      }
    }
    if (parsed) {
      out.push({
        ts: stringOr(parsed.ts ?? parsed.timestamp) ?? null,
        level: stringOr(parsed.level) ?? null,
        text: stringOr(parsed.text ?? parsed.msg ?? parsed.message) ?? line,
      });
    } else {
      out.push({ ts: null, level: null, text: line });
    }
  }
  return out;
}

/** Chunks for one stream, in sequence order. */
export function chunksForStream(
  manifest: DiagnosticManifest,
  stream: DiagnosticStream,
): DiagnosticChunkMeta[] {
  return manifest.chunks.filter((c) => c.stream === stream);
}

/** Human label for an error class (fallback: the raw code). */
export function errorClassLabel(code: AtoErrorClass | null): string {
  switch (code) {
    case "capsule_manifest_error":
      return "Capsule manifest error";
    case "recipe_resolution_error":
      return "Recipe resolution error";
    case "artifact_materialization_error":
      return "Artifact materialization error";
    case "runtime_install_error":
      return "Runtime install error";
    case "provider_capacity_error":
      return "Provider capacity error";
    case "provider_policy_error":
      return "Provider policy error";
    case "network_bind_error":
      return "Network bind error";
    case "permission_denied":
      return "Permission denied";
    case "process_crash":
      return "Process crash";
    case "healthcheck_failed":
      return "Health check failed";
    case "qa_environment_error":
      return "QA environment error";
    case "ato_internal_error":
      return "Ato internal error";
    default:
      return code ?? "Unknown error";
  }
}

/** Human label for the likely owner. */
export function likelyOwnerLabel(owner: LikelyOwner | null): string {
  switch (owner) {
    case "toml":
      return "Capsule manifest (capsule.toml)";
    case "repo":
      return "App source (repository)";
    case "ato":
      return "Ato runtime";
    case "provider":
      return "Compute provider";
    case "user_env":
      return "Your environment";
    default:
      return owner ?? "Unknown";
  }
}
